/**
 * A tiny stale-while-revalidate cache for a page's data (2026-09-22).
 *
 * THE PROBLEM IT SOLVES: this is a single-page app whose sections are routes,
 * so clicking "Budget" after "Dashboard" re-ran the entire project load —
 * twenty-odd requests — even though every byte of it was already in memory.
 * With a ~200ms per-request floor on the live database that was over a second
 * of waiting per click, for nothing.
 *
 * The rules, kept deliberately small:
 *
 *   peek()     what we have, however old — paint it immediately
 *   isFresh()  whether it is recent enough to skip the refresh entirely
 *   put()      store a load
 *   forget()   after a write, or when the row is gone
 *
 * A caller paints from `peek`, returns if `isFresh`, and otherwise refreshes
 * in the background. Stale data on screen for one second beats a spinner for
 * two — but only because it is ALWAYS followed by a refresh, and because
 * every write path calls `forget` (or loads with force).
 *
 * `maxEntries` keeps it honest: this is a per-page cache, not a store. One
 * project's workspace can be megabytes, and holding every project a designer
 * clicked through would be a memory leak with a nice name.
 */

export interface CacheEntry<T> {
  value: T;
  /** When it was stored — `Date.now()` at `put` time. */
  at: number;
}

export class PageCache<T> {
  private readonly entries = new Map<string, CacheEntry<T>>();

  constructor(private readonly maxEntries = 1) {}

  /** What we have for this key, however stale, or null. */
  peek(key: string): T | null {
    return this.entries.get(key)?.value ?? null;
  }

  /** True when the entry exists and is younger than `maxAgeMs`. */
  isFresh(key: string, maxAgeMs: number, now: number = Date.now()): boolean {
    const entry = this.entries.get(key);
    if (!entry) return false;
    // A clock that jumped backwards must not make an entry immortal.
    const age = now - entry.at;
    return age >= 0 && age < maxAgeMs;
  }

  put(key: string, value: T, now: number = Date.now()): void {
    // Re-inserting moves the key to the end, so eviction drops the
    // least-recently-STORED entry rather than an arbitrary one.
    this.entries.delete(key);
    this.entries.set(key, { value, at: now });
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next();
      if (oldest.done) break;
      this.entries.delete(oldest.value);
    }
  }

  forget(key: string): void {
    this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}

/**
 * Whether a freshly loaded page is identical to what is already on screen.
 *
 * Used to skip a repaint after a background refresh: re-rendering the whole
 * project because nothing changed would throw away scroll position and any
 * open inline editor for no reason at all.
 *
 * Compared by JSON rather than by reference because every load builds new
 * objects. Maps and Dates do not survive JSON.stringify, so they are replaced
 * with something that does — a Map of portal tokens that gains a row has to
 * count as a change.
 */
export function sameSnapshot(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return snapshot(a) === snapshot(b);
  } catch {
    // A cycle, or something that will not serialize: assume it changed and
    // repaint. Wrong-but-slower beats wrong-and-stale.
    return false;
  }
}

function snapshot(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) => {
    if (v instanceof Map) return { __map: [...v.entries()] };
    if (v instanceof Set) return { __set: [...v.values()] };
    return v;
  });
}
